// Keep route identity small: this module is part of the shared app shell.
//
// Editing these gates is a publication decision, recorded in
// docs/seo/decisions/2026-11-01-learn-library-adoption.md. The library ships
// held: every URL is registered from the first commit, and nothing publishes
// until the whole first cohort has cleared its author, underwriting and
// compliance sign-offs. Then one deploy makes all of them live together.
export const LEARN_INDEX_PATH = '/learn';

export const LEARN_SLUGS = [
  'do-dscr-loans-require-20-percent-down',
  'dscr-loan-requirements',
  'what-is-a-good-dscr',
  'dscr-loan-downsides',
  'dscr-vs-conventional-loan',
  'dscr-loan-llc',
  'how-many-dscr-loans-can-you-have',
  'dscr-loan-short-term-rental',
  'dscr-loan-refinance-cash-out',
  'who-offers-dscr-loans',
] as const;
export type LearnSlug = typeof LEARN_SLUGS[number];

/** The one article that mounts the shared DSCR calculator and so has its own page component. */
export const LEARN_CALCULATOR_ARTICLE_SLUG = 'what-is-a-good-dscr' satisfies LearnSlug;

export interface LearnPublicationEntry {
  /** Author, underwriting and compliance approvals are current for the shipped digests. */
  contentApproved: boolean;
  /** The evidence id that records those approvals; null until it exists. */
  evidence: string | null;
}

export interface LearnPublicationState {
  /** The one release switch. Flipping it publishes nothing until the whole cohort is approved. */
  releaseApproved: boolean;
  /** Every slug that must be approved before any of them publishes. */
  launchCohort: readonly LearnSlug[];
  /** Per-article sign-off record. A slug without an entry is held, cohort or not. */
  publication: Partial<Record<LearnSlug, LearnPublicationEntry>>;
  /** Post-launch withdrawals, keyed by slug, valued by the reason or evidence id. */
  holds: Partial<Record<LearnSlug, string>>;
  /** Evidence id of the verified launch. Holds are a contract violation while it is null. */
  launchCompleted: string | null;
}

/** The first release is all ten articles or nothing. */
export const LEARN_LAUNCH_COHORT: readonly LearnSlug[] = LEARN_SLUGS;

export const LEARN_PUBLICATION_STATE: LearnPublicationState = {
  releaseApproved: true,
  launchCohort: LEARN_LAUNCH_COHORT,
  publication: {
    'do-dscr-loans-require-20-percent-down': { contentApproved: true, evidence: 'learn-dscr-guideline-range-review-2026-11-01' },
    'dscr-loan-requirements': { contentApproved: true, evidence: 'learn-dscr-guideline-range-review-2026-11-01' },
    'what-is-a-good-dscr': { contentApproved: true, evidence: 'learn-dscr-guideline-range-review-2026-11-01' },
    'dscr-loan-downsides': { contentApproved: true, evidence: 'learn-dscr-guideline-range-review-2026-11-01' },
    'dscr-vs-conventional-loan': { contentApproved: true, evidence: 'learn-dscr-guideline-range-review-2026-11-01' },
    'dscr-loan-llc': { contentApproved: true, evidence: 'learn-dscr-guideline-range-review-2026-11-01' },
    'how-many-dscr-loans-can-you-have': { contentApproved: true, evidence: 'learn-dscr-guideline-range-review-2026-11-01' },
    'dscr-loan-short-term-rental': { contentApproved: true, evidence: 'learn-dscr-guideline-range-review-2026-11-01' },
    'dscr-loan-refinance-cash-out': { contentApproved: true, evidence: 'learn-dscr-guideline-range-review-2026-11-01' },
    'who-offers-dscr-loans': { contentApproved: true, evidence: 'learn-dscr-guideline-range-review-2026-11-01' },
  },
  holds: {},
  launchCompleted: 'learn-launch-verification-2026-11-02',
};

function approved(state: LearnPublicationState, slug: LearnSlug): boolean {
  const entry = state.publication[slug];
  return Boolean(entry && entry.contentApproved && entry.evidence);
}

/**
 * Pure form of the gate so the negative cases can be tested without editing
 * the committed state. The index publishes when the release is approved and
 * the whole cohort is approved; an article additionally needs its own entry
 * and must not be held.
 */
export function resolveLearnPublication(state: LearnPublicationState, slug?: LearnSlug): boolean {
  if (!state.releaseApproved) return false;
  if (!state.launchCohort.every(member => approved(state, member))) return false;
  if (!slug) return true;
  return approved(state, slug) && !(slug in state.holds);
}

/** Contract checks the unit test runs over the committed state and over synthetic ones. */
export function learnPublicationInvariantErrors(state: LearnPublicationState): string[] {
  const errors: string[] = [];
  const known = new Set<string>(LEARN_SLUGS);
  for (const key of Object.keys(state.publication)) {
    if (!known.has(key)) errors.push(`publication entry for unknown slug ${key}`);
  }
  for (const key of Object.keys(state.holds)) {
    if (!known.has(key)) errors.push(`hold for unknown slug ${key}`);
  }
  for (const member of state.launchCohort) {
    if (!known.has(member)) errors.push(`cohort names unknown slug ${member}`);
  }
  if (state.launchCompleted === null && Object.keys(state.holds).length > 0) {
    errors.push('a hold before the first launch is recorded is a cohort failure, not a hold: keep releaseApproved false instead');
  }
  if (state.launchCompleted !== null && !state.releaseApproved) {
    errors.push('a completed launch is recorded while the release is not approved');
  }
  return errors;
}

export function isLearnPublished(slug?: LearnSlug): boolean {
  return resolveLearnPublication(LEARN_PUBLICATION_STATE, slug);
}

export function isLearnPath(path: string): boolean {
  return path === LEARN_INDEX_PATH || path.startsWith(`${LEARN_INDEX_PATH}/`);
}

export function learnArticlePath(slug: LearnSlug): string {
  return `${LEARN_INDEX_PATH}/${slug}`;
}

export function learnSlugFromPath(pathname: string): LearnSlug | undefined {
  const normalized = pathname.replace(/\/+$/, '');
  if (!normalized.startsWith(`${LEARN_INDEX_PATH}/`)) return undefined;
  const slug = normalized.slice(LEARN_INDEX_PATH.length + 1);
  return (LEARN_SLUGS as readonly string[]).includes(slug) ? (slug as LearnSlug) : undefined;
}
